"use client";
import { motion } from "framer-motion";
import { useState, useEffect } from "react";
import { 
  SparklesIcon, 
  RocketLaunchIcon, 
  HeartIcon,
  ChatBubbleLeftRightIcon,
  ArrowRightIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline';

export default function CTA() {
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const fadeInUp = {
    hidden: { opacity: 0, y: isMobile ? 10 : 20 },
    show: { 
      opacity: 1, 
      y: 0, 
      transition: { duration: isMobile ? 0.3 : 0.5, ease: [0.25, 0.46, 0.45, 0.94] } 
    }
  };

  const scaleIn = {
    hidden: { opacity: 0, scale: isMobile ? 0.95 : 0.8 },
    show: { 
      opacity: 1, 
      scale: 1,
      transition: { duration: isMobile ? 0.3 : 0.6, ease: [0.25, 0.46, 0.45, 0.94] } 
    }
  };

  const staggerContainer = {
    hidden: {},
    show: {
      transition: {
        staggerChildren: isMobile ? 0.1 : 0.15
      }
    }
  };

  const features = [
    {
      icon: RocketLaunchIcon,
      title: "Démarrage rapide",
      description: "Opérationnel en minutes",
      gradient: "from-blue-500 to-cyan-500"
    },
    {
      icon: HeartIcon,
      title: "Satisfaction garantie",
      description: "95% de satisfaction utilisateur",
      gradient: "from-pink-500 to-rose-500"
    },
    {
      icon: ChatBubbleLeftRightIcon,
      title: "Support 24/7",
      description: "Assistance continue",
      gradient: "from-emerald-500 to-teal-500"
    }
  ];

  return (
    <section className={`py-16 bg-gradient-to-br from-violet-50/20 via-purple-50/20 to-gray-50/20 relative overflow-hidden ${isMobile ? 'mobile-simple' : ''}`}>
      {/* Éléments décoratifs animés - désactivés sur mobile */}
      {!isMobile && (
        <>
          {/* Cercles décoratifs supprimés */}
        </>
      )}
      
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <motion.div
          variants={staggerContainer}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: isMobile ? 0.05 : 0.1, margin: isMobile ? "-50px" : "-100px" }}
          className="text-center"
        >
          {/* Container principal avec effet glassmorphism */}
          <motion.div
            variants={scaleIn}
            whileHover={{ scale: 1.02 }}
            className="bg-gradient-to-r from-violet-600 via-purple-600 to-pink-600 rounded-3xl p-1 shadow-2xl relative overflow-hidden group"
          >
            {/* Effet de brillance animé */}
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
            
            <div className="bg-gradient-to-br from-white/95 to-white/90 backdrop-blur-sm rounded-3xl p-8 md:p-12 relative">
              {/* Titre principal */}
              <motion.h2
                variants={fadeInUp}
                className="text-3xl sm:text-4xl font-bold text-center mb-6"
              >
                <motion.span
                  animate={{ 
                    backgroundPosition: ['0% 50%', '100% 50%', '0% 50%']
                  }}
                  transition={{ 
                    duration: 3, 
                    repeat: Infinity, 
                    ease: "linear" 
                  }}
                  className="inline-block bg-gradient-to-r from-violet-600 via-purple-600 to-pink-600 bg-[length:200%_100%] bg-clip-text text-transparent"
                >
                  Prêt à révolutionner
                </motion.span>
                <span className="block text-gray-900 mt-2">
                  votre onboarding RH ?
                </span>
              </motion.h2>

              {/* Description */}
              <motion.p
                variants={fadeInUp}
                className="text-xl text-gray-600 mb-10 max-w-3xl mx-auto leading-relaxed text-center"
              >
                Découvrez comment ConnectRH peut{" "}
                <span className="font-semibold bg-gradient-to-r from-violet-600 to-purple-600 bg-clip-text text-transparent">
                  transformer l'expérience
                </span>{" "}
                de vos collaborateurs et libérer le potentiel de votre équipe RH.
              </motion.p>

              {/* Boutons d'action */}
              <motion.div
                variants={fadeInUp}
                className="flex flex-col sm:flex-row gap-4 justify-center items-center"
              >
                <motion.a
                  href="/chat"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="inline-flex items-center space-x-3 bg-gradient-to-r from-violet-600 to-purple-600 text-white px-8 py-4 rounded-2xl font-bold shadow-xl hover:shadow-2xl transition-all duration-300 group relative overflow-hidden"
                >
                  {/* Effet de brillance */}
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-500"></div>
                  
                  <motion.div
                    animate={{ rotate: 360 }}
                    transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
                  >
                    <SparklesIcon className="w-5 h-5" />
                  </motion.div>
                  <span className="relative">Essayer ConnectRH</span>
                  <motion.div
                    animate={{ x: [0, 5, 0] }}
                    transition={{ duration: 1.5, repeat: Infinity }}
                  >
                    <ArrowRightIcon className="w-5 h-5" />
                  </motion.div>
                </motion.a>

                <motion.a
                  href="/contact"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="inline-flex items-center space-x-2 bg-white border-2 border-violet-300 text-violet-600 px-8 py-4 rounded-2xl font-semibold hover:bg-violet-50 transition-all duration-300 shadow-lg"
                >
                  <ChatBubbleLeftRightIcon className="w-5 h-5" />
                  <span>Nous contacter</span>
                </motion.a>
              </motion.div>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
}
